import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useT } from '../hooks/useT';
import { useSettings } from '../state/SettingsContext';
import { useRouter } from '../state/RouterContext';
import { useTts } from '../state/TtsContext';

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */

interface AppHeaderProps {
  title: string;
  subtitle?: string;
  showBack?: boolean;
  /** Extra control on the far side, e.g. the settings gear on Home. */
  action?: ReactNode;
  /** Called before navigating back (used to stop audio). */
  onBack?: () => void;
}

export function AppHeader({ title, subtitle, showBack = true, action, onBack }: AppHeaderProps) {
  const t = useT();
  const { back, depth } = useRouter();
  const { stop } = useTts();

  return (
    <header className="app-header">
      <div className="app-header__inner">
        {showBack && depth > 1 ? (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              stop();
              onBack?.();
              back();
            }}
          >
            <span className="btn__icon" aria-hidden="true">
              ⟨
            </span>
            {t('back')}
          </button>
        ) : null}
        <h1 className="app-header__title">
          {title}
          {subtitle ? <span className="app-header__sub">{subtitle}</span> : null}
        </h1>
        {action}
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Progress bar                                                        */
/* ------------------------------------------------------------------ */

export function ProgressBar({
  value,
  max,
  label,
  tone = 'primary',
}: {
  value: number;
  max: number;
  label?: string;
  tone?: 'primary' | 'success';
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="progress-line">
      {label ? <span>{label}</span> : null}
      <div
        className="progress-track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
      >
        <div className={`progress-fill${tone === 'success' ? ' progress-fill--success' : ''}`} style={{ width: `${pct}%` }} />
      </div>
      <Fraction value={value} max={max} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Fraction                                                            */
/* ------------------------------------------------------------------ */

/**
 * "29 / 40" written inside an Arabic (right-to-left) paragraph is reordered by
 * the bidi algorithm and reads as "40 / 29", which is exactly backwards for a
 * score. Isolating it as its own left-to-right run keeps the numbers in the
 * order they were written, without disturbing the Arabic around it.
 */
export function Fraction({ value, max }: { value: number; max: number }) {
  return (
    <span className="ltr" dir="ltr">
      {value} / {max}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* No-voice banner                                                     */
/* ------------------------------------------------------------------ */

/** Tells the user plainly when speech is not available, instead of going silent. */
export function VoiceWarning() {
  const t = useT();
  const { canSpeak, status, speechLang } = useTts();
  const { settings } = useSettings();

  if (canSpeak) return null;

  const engineOffline = status.engineId === 'piper' && status.error;

  return (
    <div className="notice notice--warn" role="status">
      <div className="notice__title">
        <span aria-hidden="true">🔇 </span>
        {speechLang === 'ar' ? t('noArabicVoiceTitle') : t('audioUnavailable')}
      </div>
      <p style={{ margin: 0 }}>
        {engineOffline
          ? `${settings.piperUrl} — ${status.error}`
          : speechLang === 'ar'
            ? t('noArabicVoiceBody')
            : t('noEnglishVoice')}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirm dialog                                                      */
/* ------------------------------------------------------------------ */

export function ConfirmDialog({
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  tone = 'danger',
}: {
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  tone?: 'danger' | 'primary';
}) {
  const t = useT();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onCancel}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={message}
        onClick={(e) => e.stopPropagation()}
      >
        <p style={{ fontWeight: 700, fontSize: '1.05rem' }}>{message}</p>
        <div className="row">
          <button ref={ref} type="button" className="btn" onClick={onCancel}>
            {t('cancel')}
          </button>
          <div className="spacer" />
          <button
            type="button"
            className={`btn ${tone === 'danger' ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Toast                                                               */
/* ------------------------------------------------------------------ */

export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDone, 3200);
    return () => window.clearTimeout(timer);
  }, [message, onDone]);

  return (
    <div className="toast" role="status" aria-live="polite">
      {message}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Demo content notice                                                 */
/* ------------------------------------------------------------------ */

export function DemoNotice() {
  const t = useT();
  return (
    <div className="notice" role="note">
      <span className="badge badge--demo">{t('demoBadge')}</span>
      <p style={{ margin: 'var(--space-2) 0 0', fontSize: '0.85rem' }}>{t('demoNotice')}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

export function EmptyState({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div className="card stack" style={{ textAlign: 'center' }}>
      <p style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{message}</p>
      {action}
    </div>
  );
}
